import { useCallback, useEffect, useState } from 'react'
import { Loader2, AlertTriangle, ShieldCheck, FileText, CheckCircle2 } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { warningState, warningLabel, warningTone, needsAck, fmtDay, fmtStamp, ACK_CONFIRM_TH } from '../../lib/warning.js'
import { decNotice, refreshNotice } from '../../lib/notice.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * พื้นที่ของฉัน › ฝ่ายบุคคล › หนังสือเตือน — the warnings issued to this employee.
 *
 * 🔑 Opening the page does NOT clear the notification. A warning has to be
 * ACKNOWLEDGED explicitly, because with no printed letter (owner's decision) that
 * timestamped click is the clinic's only evidence of delivery — the one thing on this
 * screen that must never happen as a side effect of navigation.
 */
export default function MeWarning() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const w = await apiGet('/api/me/warning')
      setRows(Array.isArray(w?.items) ? w.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดข้อมูลไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) {
    return <div className="py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
  }

  return (
    <div className="max-w-3xl space-y-5">
      {err && <p className="text-[13px] text-rose-600">{err}</p>}
      <WarningSection rows={rows} onChanged={load} />
    </div>
  )
}

function WarningSection({ rows, onChanged }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={15} className="text-slate-400" />
        <h2 className="text-[14px] font-semibold text-slate-800">หนังสือเตือน</h2>
      </div>
      {rows.length === 0 ? (
        <div className="py-6 text-center">
          <ShieldCheck size={22} className="mx-auto text-emerald-400 mb-1.5" />
          <p className="text-[13px] text-slate-400">ไม่มีหนังสือเตือน</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((w) => <WarningCard key={w.id} w={w} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  )
}

function WarningCard({ w, onChanged }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState(w.acknowledged_note || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const state = warningState(w)

  async function acknowledge() {
    setOpen(false)
    setBusy(true)
    setErr('')
    try {
      await postJson(`/api/me/warning/${w.id}`, { note: note.trim() })
      decNotice('warnings_unack', 1)
      refreshNotice()
      await onChanged?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกการรับทราบไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`rounded-xl border px-3.5 py-3 ${state === 'voided' ? 'border-slate-200 bg-slate-50/60' : 'border-slate-200'}`}>
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <div className={`text-[13.5px] font-semibold ${state === 'voided' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
            {w.subject}
          </div>
          <div className="text-[11.5px] text-slate-400 mt-0.5">
            ออกเมื่อ {fmtDay(w.issue_date || w.created_at)}
            {w.incident_date && <> · เหตุเกิด {fmtDay(w.incident_date)}</>}
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 shrink-0 ${warningTone(w)}`}>
          {warningLabel(w)}
        </span>
      </div>

      {w.detail && (
        <p className="mt-2 text-[13px] text-slate-700 whitespace-pre-wrap break-words">{w.detail}</p>
      )}

      {Array.isArray(w.file) && w.file.length > 0 && (
        <div className="mt-2 space-y-1">
          {w.file.map((f) => (
            <a
              key={f.uuid || f.location}
              href={`/api/me/file/${encodeURI(f.location || '')}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-[12.5px] text-brand-600 hover:text-brand-700 hover:underline"
            >
              <FileText size={13} /> {f.name || 'เอกสารแนบ'}
            </a>
          ))}
        </div>
      )}

      {state === 'voided' && (
        <p className="mt-2 text-[12px] text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1.5">
          ยกเลิกเมื่อ {fmtStamp(w.voided_at)}{w.void_reason ? ` — ${w.void_reason}` : ''}
        </p>
      )}

      {state === 'acknowledged' && (
        <div className="mt-2 text-[12px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg px-2.5 py-1.5">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 size={12} /> รับทราบเมื่อ {fmtStamp(w.acknowledged_at)}
          </div>
          {w.acknowledged_note && (
            <div className="mt-1 text-slate-600 whitespace-pre-wrap break-words">
              ความเห็นของท่าน: {w.acknowledged_note}
            </div>
          )}
        </div>
      )}

      {needsAck(w) && (
        <div className="mt-3 rounded-xl bg-amber-50/70 ring-1 ring-amber-200 px-3 py-2.5">
          <p className="text-[12px] text-amber-800 leading-relaxed">{ACK_CONFIRM_TH}</p>
          <textarea
            rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="ความเห็นของท่าน (ไม่บังคับ) — เช่น รับทราบแต่ไม่เห็นด้วย เพราะ…"
            className={`${FIELD} mt-2 resize-y`}
          />
          {err && <p className="mt-1.5 text-[12px] text-rose-600">{err}</p>}
          <div className="mt-2 flex justify-end">
            <button
              type="button" onClick={() => setOpen(true)} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} รับทราบ
            </button>
          </div>
        </div>
      )}

      {open && (
        <ConfirmDialog
          title="ยืนยันการรับทราบ"
          message={`“${w.subject}”\n\nระบบจะบันทึกวันเวลาที่ท่านกดรับทราบไว้เป็นหลักฐาน${note.trim() ? '\n\nพร้อมความเห็นของท่านที่กรอกไว้' : ''}`}
          confirmLabel="รับทราบ"
          onConfirm={acknowledge}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  )
}

